import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface PrivacyNoticeStatus {
  acknowledged: boolean;
  acknowledgedAt: string | null;
  invitedAt: string | null;
  inviteeEmail: string | null;
  inviteeFirstName: string | null;
  inviteeLastName: string | null;
  invitedByEmail: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PrivacyNoticeService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly baseUrl = `${environment.coreApiUrl}/api/v1/user-management/privacy-notice/me`;

  private cachedStatus: PrivacyNoticeStatus | null = null;
  private cachedUserEmail: string | null = null;

  getStatus(forceRefresh: boolean = false): Observable<PrivacyNoticeStatus> {
    if (!forceRefresh && this.hasValidCache()) {
      return of(this.cachedStatus as PrivacyNoticeStatus);
    }

    return this.http.get<any>(this.baseUrl).pipe(
      map(response => this.normalizeStatus(response)),
      tap(status => this.updateCache(status)),
    );
  }

  acknowledge(): Observable<PrivacyNoticeStatus> {
    return this.http.post<any>(`${this.baseUrl}/acknowledge`, {}).pipe(
      map(response => {
        const normalized = this.normalizeStatus(response);
        return {
          ...normalized,
          acknowledged: true,
          acknowledgedAt: normalized.acknowledgedAt || new Date().toISOString(),
        };
      }),
      tap(status => this.updateCache(status)),
    );
  }

  isAcknowledgedInCache(): boolean {
    return this.hasValidCache() && this.cachedStatus?.acknowledged === true;
  }

  private hasValidCache(): boolean {
    return !!this.cachedStatus && this.cachedUserEmail === this.authService.getCurrentUser()?.email;
  }

  private updateCache(status: PrivacyNoticeStatus): void {
    this.cachedStatus = status;
    this.cachedUserEmail = this.authService.getCurrentUser()?.email || null;
  }

  private normalizeStatus(response: any): PrivacyNoticeStatus {
    const data = response?.data ?? response ?? {};

    return {
      acknowledged: Boolean(
        data.acknowledged
        ?? data.privacyNoticeAccepted
        ?? data.flagInformativaPrivacy
        ?? data.privacyNoticeViewed,
      ),
      acknowledgedAt: data.acknowledgedAt ?? data.privacyNoticeAcceptedAt ?? null,
      invitedAt: data.invitedAt ?? data.invitationCreatedAt ?? null,
      inviteeEmail: data.inviteeEmail ?? data.email ?? null,
      inviteeFirstName: data.inviteeFirstName ?? data.firstName ?? null,
      inviteeLastName: data.inviteeLastName ?? data.lastName ?? null,
      invitedByEmail: data.invitedByEmail ?? data.inviterEmail ?? null,
    };
  }
}